/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import { SUPER_ADMIN_WORKSPACE } from "./groups";

export const ROLE_SEPARATOR = ":";

export enum RoleEnum {
  PUBLIC = "Public",
  VIEWER = "Viewer",
  EDITOR = "Editor",
  ADMIN = "Admin",
  OWNER = "Owner",
  SUPER_ADMIN = "SuperAdmin",
}

/**
 * Is Super-Admin if special permission '*:SuperAdmin' present.
 *
 * @param roles
 * @param sep
 */
export const isSuperAdmin = (
  roles: string[] | object,
  sep: string = ROLE_SEPARATOR
): boolean =>
  Array.isArray(roles)
    ? roles.includes(`${SUPER_ADMIN_WORKSPACE}${sep}${RoleEnum.SUPER_ADMIN}`)
    : !!roles?.hasOwnProperty?.(SUPER_ADMIN_WORKSPACE) &&
      roles[SUPER_ADMIN_WORKSPACE].includes(RoleEnum.SUPER_ADMIN);

/**
 * Authorized if any role present.
 * @param roles
 */
export const isAuthz = (roles: string[]): boolean => {
  return roles && roles.length > 0;
};

/**
 * Authorized if any role except 'Public' & 'Viewer' present.
 * @param roles
 * @param sep
 */
export const isAdminAuthz = (
  roles: string[],
  sep: string = ROLE_SEPARATOR
): boolean => {
  return roles.some(
    (r: string) =>
      ![RoleEnum.PUBLIC, RoleEnum.VIEWER].includes(<any>r.split(sep)[1])
  );
};

/**
 * Map groups from existing roles.
 * @param roles
 * @param excludeGroups
 * @param sep
 */
export const mapRoleGroups = (
  roles: string[],
  excludeGroups: string[] = [],
  sep: string = ROLE_SEPARATOR
): string[] => {
  const groups = roles
    .map((r: string) => r.split(sep)[0])
    .filter((g: string) => !excludeGroups.includes(g));

  return Array.from(new Set(groups));
};

/**
 * Map groups from existing roles.
 * Group isAuthorized if any role except 'Public' & 'Viewer' present.
 * @param roles
 * @param excludeGroups
 *
 * @param sep
 */
export const mapAuthorizedRoleGroups = (
  roles: string[],
  excludeGroups: string[] = [],
  sep: string = ROLE_SEPARATOR
): string[] => {
  const groups = roles
    .filter(
      (r: string) =>
        ![RoleEnum.PUBLIC, RoleEnum.VIEWER].includes(<any>r.split(sep)[1])
    )
    .map((r: string) => r.split(sep)[0])
    .filter((g: string) => !excludeGroups.includes(g));
  return Array.from(new Set(groups));
};

/**
 *  Returns true if user has permission to view admin link.
 */
export const checkAdminRole = (roles: string[]): boolean => {
  const filtered: string[] = Object.values(RoleEnum).filter(
    (r: any) => ![RoleEnum.PUBLIC, RoleEnum.VIEWER].includes(r)
  );
  return roles.some((r: string) => filtered.includes(r));
};

/**
 * Returns a list of groups where role >= VIEWER
 */
export const getPrivateGroups = (mappedRoles): string[] => {
  return Object.keys(mappedRoles)
    .filter(filterNotSuperAdmin)
    .filter(
      (group) =>
        !(
          mappedRoles[group].length === 1 &&
          mappedRoles[group][0].endsWith(RoleEnum.PUBLIC)
        )
    );
};

/**
 * Returns a list of groups where the user has PUBLIC role
 */
export const getPublicGroups = (mappedRoles): string[] => {
  return Object.keys(mappedRoles)
    .filter(filterNotSuperAdmin)
    .filter((group) => mappedRoles[group].some((x) => x === RoleEnum.PUBLIC));
};

const filterNotSuperAdmin = (group) => group !== SUPER_ADMIN_WORKSPACE;

/**
 * Checks whether the user has has the SuperAdmin role for the group or
 * the platform.
 */
export const isSuperAdminToWorkspace = (
  roles: { [key: string]: any },
  group: string
): boolean => {
  return Object.entries(roles).some(
    ([workspace, roles]) =>
      (workspace === group || workspace === SUPER_ADMIN_WORKSPACE) &&
      roles.includes(RoleEnum.SUPER_ADMIN)
  );
};
